import {
  AvatarUploadUrlRequest,
  AvatarUploadUrlResponse,
  AvatarCompleteRequest,
  WalletChallengeRequest,
} from "@interfaces";
import {
  PrivateUserProfileView,
  UserProfileUpdate,
  PublicUserProfileView,
  XRole,
} from "@bbb/model";
import { _apiRequest } from "./_apiClients";

export type MyBlocksAuditReasonSummary = {
  reason: "quest_completed" | "achievement_unlocked" | "referral_completed" | "other";
  totalDelta: number;
  eventCount: number;
};

export type MyBlocksAuditCategorySummary = {
  category:
    | "quest_completion"
    | "achievement_unlocked"
    | "referral_completion";
  label: string;
  eventCount: number;
};

export type MyBlocksAuditEvent = {
  id: string;
  reason: "quest_completed" | "achievement_unlocked" | "referral_completed" | "other";
  referenceId: string | null;
  delta: number;
  createdAt: number | null;
};

export type MyBlocksAuditView = {
  blockBalance: number;
  recentLimit: number;
  categories: MyBlocksAuditCategorySummary[];
  recentEvents: MyBlocksAuditEvent[];
  summary: MyBlocksAuditReasonSummary[];
};

export function getMyProfile(token?: string, signal?: AbortSignal) {
  return _apiRequest<PrivateUserProfileView>("/v1/me", {
    token,
    signal,
  });
}

export function updateMyProfile(
  token: string | undefined,
  payload: UserProfileUpdate,
  signal?: AbortSignal
) {
  return _apiRequest<PrivateUserProfileView>("/v1/me", {
    method: "PUT", //TODO use patch
    token,
    data: payload,
    signal,
  });
}

export function getPublicUserProfile(
  userId: string,
  token?: string | null,
  recaptchaToken?: string,
  recaptchaAction?: string,
) {
  const headers: Record<string, string> = {};

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (recaptchaToken) {
    headers["x-recaptcha-token"] = recaptchaToken;
  }
  if (recaptchaAction) {
    headers["x-recaptcha-action"] = recaptchaAction;
  }

  return _apiRequest<PublicUserProfileView>(`/v1/users/${userId}`, {
    authRequired: false,
    headers: Object.keys(headers).length ? headers : undefined,
  });
}

export type PublicCampaignNft = {
  id: string;
  campaignId: string;
  communityId: string;
  userId: string;
  contractAddress?: string;
  chainId?: number;
  tokenId?: string;
  txHash?: string;
  mintedAt?: number;
};

export function listPublicUserNfts(
  userId: string,
  token?: string | null,
  recaptchaToken?: string,
  recaptchaAction?: string,
) {
  const headers: Record<string, string> = {};

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (recaptchaToken) {
    headers["x-recaptcha-token"] = recaptchaToken;
  }
  if (recaptchaAction) {
    headers["x-recaptcha-action"] = recaptchaAction;
  }

  return _apiRequest<PublicCampaignNft[]>(`/v1/users/${userId}/nfts`, {
    authRequired: false,
    headers: Object.keys(headers).length ? headers : undefined,
  });
}

export function confirmEmailVerification(token: string) {
  return _apiRequest<EmailVerificationResult>(
    "/v1/me/confirm-email-verification",
    {
      method: "POST",
      token,
    }
  );
}

export function getMyBlocksAudit(limit = 3, signal?: AbortSignal) {
  return _apiRequest<MyBlocksAuditView>("/v1/me/blocks-audit", {
    params: { limit },
    signal,
  });
}


//#region Avatar

export function requestAvatarUploadUrl(
  token: string | undefined,
  payload: AvatarUploadUrlRequest
) {
  return _apiRequest<AvatarUploadUrlResponse>("/v1/me/avatar/upload-url", {
    method: "POST",
    token,
    data: payload,
  });
}

export function completeAvatarUpload(
  token: string | undefined,
  payload: AvatarCompleteRequest
) {
  return _apiRequest<PrivateUserProfileView>("/v1/me/avatar/complete", {
    method: "POST",
    token,
    data: payload,
  });
}

export function deleteAvatar(token?: string) {
  return _apiRequest<PrivateUserProfileView>("/v1/me/avatar/delete", {
    method: "POST", //TODO use delete
    token,
  });
}

//#endregion Avatar

//#region X

export function startXAuth(
  token: string | undefined,
  payload: { xRole: XRole; communityId?: string; returnTo?: string }
) {
  return _apiRequest<{ url: string; state: string }>("/v1/x/start", {
    method: "POST",
    token,
    data: payload,
  });
}

export function startDiscordAuth(
  token: string | undefined,
  payload: { role: XRole; communityId?: string; returnTo?: string }
) {
  return _apiRequest<{ url: string; state: string }>("/v1/discord/start", {
    method: "POST",
    token,
    data: payload,
  });
}

export function disconnectX(token?: string) {
  return _apiRequest<PrivateUserProfileView>("/v1/x", {
    method: "DELETE",
    token,
  });
}

export function disconnectDiscord(token?: string) {
  return _apiRequest<PrivateUserProfileView>("/v1/discord", {
    method: "DELETE",
    token,
  });
}
//#endregion X

//#region WalletConnect

export function requestWalletChallenge(
  token: string | undefined,
  payload: WalletChallengeRequest,
  signal?: AbortSignal
) {
  return _apiRequest<{ nonce: string; message: string }>(
    "/v1/me/wallets/challenge",
    {
      method: "POST",
      token,
      data: payload,
      signal,
    }
  );
}

//#endregion WalletConnect
